/**
 * Style and Base type — the template's Details pickers, shared by the wizard and the Generate
 * page so both read the same lists and follow the same "+ New" rules.
 */
import { useCallback, useEffect, useState } from "react";
import { create } from "zustand";
import { HttpError, type CaseStyleOption } from "@genz/platform";
import { useServices } from "../app/context.js";
import { BASE_TYPES } from "../editor/baseType.js";
import { loadCatalogList } from "../templates/catalogOptions.js";
import { notifyCatalogChanged } from "../templates/catalogInvalidation.js";
import { CatalogSelect } from "../ui/CatalogSelect.js";
import { ErrorNotice } from "../ui/ErrorNotice.js";
import { Icon } from "../ui/Icon.js";
import { InlineCreateRow } from "../ui/InlineCreateRow.js";
import { InlineRetry } from "../ui/InlineRetry.js";

// One list for every picker on screen: a style added in one shows in the others.
const useCaseStyleList = create<{ styles: CaseStyleOption[]; error: boolean }>(() => ({ styles: [], error: false }));

/** The case-style catalog (`GET /api/case-styles`), loaded on mount. */
export function useCaseStyles(): {
  styles: CaseStyleOption[];
  error: boolean;
  reload(): Promise<void>;
  add(label: string): Promise<CaseStyleOption>;
} {
  const { client, logger } = useServices();
  const { styles, error } = useCaseStyleList();

  const reload = useCallback(async () => {
    const r = await loadCatalogList("case_styles", () => client.listCaseStyles(), logger);
    useCaseStyleList.setState({ styles: r.data, error: !!r.error });
  }, [client, logger]);
  useEffect(() => { void reload(); }, [reload]);

  const add = async (label: string) => {
    const code = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || `style-${Date.now()}`;
    const created = await client.createCaseStyle({ code, label });
    notifyCatalogChanged("caseStyle");
    useCaseStyleList.setState((s) => ({
      styles: s.styles.some((c) => c.id === created.id) ? s.styles : [...s.styles, created],
    }));
    return created;
  };

  return { styles, error, reload, add };
}

/** The Style field. `allowAdd` (admins) shows "+ New", an edit-in-place row that saves the
 *  style to the catalog and selects it. Without `onError` a failed add is shown in the field. */
export function StylePicker({ value, onChange, testIdBase, allowAdd, onError }: {
  value: string;
  onChange: (id: string) => void;
  testIdBase: string;
  allowAdd: boolean;
  onError?: (message: string | null) => void;
}) {
  const { styles, error, reload, add } = useCaseStyles();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const report = onError ?? setAddError;
  const cancel = () => {
    setAdding(false);
    setName("");
  };
  const submit = async () => {
    const label = name.trim();
    if (!label) return;
    report(null);
    try {
      onChange((await add(label)).id);
      cancel();
    } catch (e) {
      report(e instanceof HttpError && e.status === 403
        ? "Only an admin can add a case style."
        : e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <label className="field">
      <span className="label">
        Style
        {error && <InlineRetry onRetry={() => void reload()} testId={`${testIdBase}-style-retry`} />}
      </span>
      <div className="inline-create-row">
        {adding ? (
          <div className="new-style" role="group" aria-label="New case style">
            <input
              className="input new-style__input"
              type="text"
              autoFocus
              value={name}
              placeholder='New case style (e.g. "Clear TPU")'
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); void submit(); }
                else if (e.key === "Escape") { e.preventDefault(); cancel(); }
              }}
              data-testid="new-style-input"
              aria-label="New case style name"
            />
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => void submit()}
              disabled={!name.trim()}
              data-testid="new-style-add"
            >
              Add
            </button>
            <button type="button" className="btn btn--sm btn--ghost" onClick={cancel} data-testid="new-style-cancel">
              Cancel
            </button>
          </div>
        ) : (
          <>
            <CatalogSelect
              value={value}
              onChange={onChange}
              testId={`${testIdBase}-style`}
              error={error}
              options={styles.map((c) => ({ value: c.id, label: c.label }))}
            />
            {allowAdd && (
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                onClick={() => { setName(""); setAdding(true); }}
                title="Add a new case style"
                data-testid={`${testIdBase}-add-style`}
              >
                <Icon name="plus" size={16}/> New
              </button>
            )}
          </>
        )}
      </div>
      {addError && <span className="field-error" role="alert"><ErrorNotice detail={addError} /></span>}
    </label>
  );
}

/** The Base type field: the fixed `BASE_TYPES`, plus a free-text custom type where `allowAdd`. */
export function BaseTypePicker({ value, onChange, testIdBase, allowAdd = false }: {
  value: string;
  onChange: (value: string) => void;
  testIdBase: string;
  allowAdd?: boolean;
}) {
  const select = (
    <select className="input" value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={`${testIdBase}-base-type`}>
      <option value="">—</option>
      {BASE_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
      {value && !BASE_TYPES.some((b) => b.value === value) && <option value={value}>{value}</option>}
    </select>
  );
  return (
    <label className="field">
      <span className="label">Base type</span>
      {allowAdd ? (
        <InlineCreateRow
          label="Use a custom base type"
          fields={[{
            key: "value",
            placeholder: 'Custom base type (e.g. "carbon fiber")',
            ariaLabel: "Custom base type",
            // LB-2: `templates.base_type` is String(30) — see the editor's row.
            maxLength: 30,
          }]}
          onSubmit={async (v) => onChange(v.value!)}
          onError={() => undefined}
          testIdBase={`${testIdBase}-new-base-type`}
          // review-1 F-3: a surface that commits from its ActionBar keeps this row's Add plain.
          submitVariant="plain"
        >
          {select}
        </InlineCreateRow>
      ) : select}
    </label>
  );
}
